import { ChevronRight } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CatalogMetadata } from '@/components/catalog'
import { LocalizedLink } from '@/components/LocalizedLink'
import { ShellPlaceholder, NotFound } from '@/components/layout/SiteShell'
import { ProductMedia } from '@/components/product/ProductMedia'
import { RelatedProducts, pilotRelated, type PilotProductId } from '@/components/product/RelatedProducts'
import { EmotraceBody, DcBody, X12Body } from '@/components/product/PilotBodies'
import { TraceabilityBody } from '@/components/product/TraceabilityBodies'
import { InfertilityBody } from '@/components/product/InfertilityBodies'
import { PreanalyticsBody } from '@/components/product/PreanalyticsBodies'
import { MicrobiologyBody } from '@/components/product/MicrobiologyBodies'
import { CollectionSystemBody } from '@/components/product/CollectionSystemBodies'
import { PointOfCareBody } from '@/components/product/PointOfCareBodies'
import { LaboratoryEquipmentBody } from '@/components/product/LaboratoryEquipmentBodies'
import { TransportSystemBody } from '@/components/product/TransportSystemBodies'
import { isTraceabilityProductId, traceabilityProducts } from '@/data/product-pages/traceability'
import { infertilityProducts, isInfertilityProductId } from '@/data/product-pages/infertility'
import { isPreanalyticsProductId, preanalyticsProducts } from '@/data/product-pages/preanalytics'
import { isMicrobiologyProductId, microbiologyProducts } from '@/data/product-pages/microbiology'
import { collectionSourceLocale, collectionSystemProducts, isCollectionSystemProductId } from '@/data/product-pages/collection-systems'
import { isPointOfCareProductId, pointOfCareProducts, pointOfCareSourceLocale } from '@/data/product-pages/point-of-care'
import { isLaboratoryEquipmentProductId, laboratoryEquipmentProducts, laboratoryEquipmentSourceLocale } from '@/data/product-pages/laboratory-equipment'
import { isTransportSystemProductId, transportSourceLocale, transportSystemProducts } from '@/data/product-pages/transport-systems'
import { contentRoutes } from '@/i18n/content-routes'
import { getLocalizedPath, languageFromPath } from '@/i18n/routes'
import { publicProducts } from '@/data/public-catalog'

const pilots = {
  'prd-emotrace-tracciabilita-sangue': { key: 'emotrace', media: '/media/source/images/EMOtrace-2.png' },
  'prd-dc-2000-sl-stappatore': { key: 'dc', media: '/media/source/images/DC2000SL-1.png' },
  'prd-x12-pro-semen-analysis-system': { key: 'x12', media: '/media/source/images/X12-1.png' },
} as const

type ProductDefinition = {
  key: 'emotrace' | 'dc' | 'x12' | 'esapoct' | 'samtrace' | 'ckup' | 'ca0ca1' | 'r10' | 'r11' | 'x3' | 'eugenio' | 'flashbot' | 'tubeRack' | 'tipos' | 'tt2000' | 'tt2000Iq' | 'faN' | 'af300' | 'accessori' | 'esaliva' | 'provette' | 'x1' | 'accre8' | 'easyNat' | 'poch100i' | 'sd1' | 'sg1' | 'coldRooms' | 'centric350' | 'cf108' | 'mf48' | 'freezers' | 'bloodBankStorage' | 'portableColdChain' | 'medicalRefrigerators' | 'dualTemperature' | 'transportEugenio' | 'coolingPlates' | 'esatrace' | 'tertiaryEndmed' | 'biosAccessories' | 'plasmaPack' | 'bag' | 'kartell' | 'slimline' | 'tertiary' | 'biosContainers'
  media: string
}

export function ProductPage() {
  const { t } = useTranslation('common')
  const location = useLocation()
  const language = languageFromPath(location.pathname)
  const route = contentRoutes.find(item => item.type === 'product' && item[language] === location.pathname.replace(/\/$/, ''))
  if (!route) return <NotFound />
  const isPilot = route.id in pilotRelated
  const traceability = isTraceabilityProductId(route.id) ? traceabilityProducts[route.id] : null
  const infertility = isInfertilityProductId(route.id) ? infertilityProducts[route.id] : null
  const preanalytics = isPreanalyticsProductId(route.id) ? preanalyticsProducts[route.id] : null
  const microbiology = isMicrobiologyProductId(route.id) ? microbiologyProducts[route.id] : null
  const collectionSystem = isCollectionSystemProductId(route.id) ? collectionSystemProducts[route.id] : null
  const pointOfCare = isPointOfCareProductId(route.id) ? pointOfCareProducts[route.id] : null
  const laboratoryEquipment = isLaboratoryEquipmentProductId(route.id) ? laboratoryEquipmentProducts[route.id] : null
  const transportSystem = isTransportSystemProductId(route.id) ? transportSystemProducts[route.id] : null
  if (!isPilot && !traceability && !infertility && !preanalytics && !microbiology && !collectionSystem && !pointOfCare && !laboratoryEquipment && !transportSystem) return <ShellPlaceholder />
  const productId = route.id
  const productRecord = traceability || infertility || preanalytics || microbiology || collectionSystem || pointOfCare || laboratoryEquipment || transportSystem
  const definition: ProductDefinition = productRecord
    ? { key: productRecord.key, media: productRecord.primaryMedia }
    : pilots[route.id as PilotProductId]
  const { key, media } = definition
  const product = publicProducts.find(item => item.id === productId)!
  const category = contentRoutes.find(item => item.id === product.categoryId)!
  const sourceLanguage = collectionSystem
    ? collectionSourceLocale(route.id as keyof typeof collectionSystemProducts, language)
    : pointOfCare
      ? pointOfCareSourceLocale(route.id as keyof typeof pointOfCareProducts, language)
    : laboratoryEquipment
      ? laboratoryEquipmentSourceLocale(route.id as keyof typeof laboratoryEquipmentProducts, language)
    : transportSystem
      ? transportSourceLocale(route.id as keyof typeof transportSystemProducts, language)
    : Boolean(traceability || infertility || microbiology || (preanalytics && preanalytics.key !== 'eugenio')) && language === 'en' ? 'it' : language
  const usesFallback = sourceLanguage !== language
  const title = t(`productPage.${key}.title`, { lng: sourceLanguage })
  const description = usesFallback
    ? t(sourceLanguage === 'it' ? 'productPage.fallbackMeta' : 'productPage.fallbackEnglishMeta', { product: title })
    : t('productPage.metaDescription', { product: title, description: t(`productPage.${key}.description`) })
  const subject = t('productPage.contactSubject', { product: title })
  const query = new URLSearchParams({ product: productId, subject })
  return <div className={`product-page product-page--${key}`}>
    <CatalogMetadata language={language} title={title} description={description} canonicalPath={route[language]} alternatePaths={{ it: route.it, en: route.en }} />
    <header className="shell-container product-opening internal-hero internal-hero--product">
      <nav className="catalog-breadcrumb" aria-label={t('catalog.breadcrumbLabel')}><ol>
        <li><LocalizedLink to="home">{t('nav.home')}</LocalizedLink></li>
        <li><ChevronRight aria-hidden="true" size={16} /><LocalizedLink to="solutions">{t('nav.solutions')}</LocalizedLink></li>
        <li><ChevronRight aria-hidden="true" size={16} /><LocalizedLink to={category.id}>{t(`categories.${category.id}`)}</LocalizedLink></li>
        <li aria-current="page"><ChevronRight aria-hidden="true" size={16} /><span lang={usesFallback ? sourceLanguage : undefined}>{title}</span></li>
      </ol></nav>
      <div className="product-opening-grid">
        <div className="product-heading"><h1 tabIndex={-1} lang={usesFallback ? sourceLanguage : undefined}>{title}</h1>
          {usesFallback && <p className="catalog-source-status">{t(sourceLanguage === 'it' ? 'productPage.sourceFallback' : 'productPage.sourceEnglishFallback')}</p>}
          <div lang={usesFallback ? sourceLanguage : undefined}><p className="product-description">{t(`productPage.${key}.description`, { lng: sourceLanguage })}</p><p className="product-distribution">{t(`productPage.${key}.distribution`, { lng: sourceLanguage })}</p></div>
        </div>
        <ProductMedia key={media} path={media} altKey={`productPage.${key}.imageAlt`} eager />
      </div>
    </header>
    {traceability
      ? <TraceabilityBody productKey={traceability.key} language={language} />
      : infertility
        ? <InfertilityBody productKey={infertility.key} language={language} />
        : preanalytics
          ? <PreanalyticsBody productKey={preanalytics.key} language={language} />
          : microbiology
            ? <MicrobiologyBody productKey={microbiology.key} language={language} />
            : collectionSystem
              ? <CollectionSystemBody productKey={collectionSystem.key} language={language} />
              : pointOfCare
                ? <PointOfCareBody productKey={pointOfCare.key} language={language} />
              : laboratoryEquipment
                ? <LaboratoryEquipmentBody productKey={laboratoryEquipment.key} language={language} />
              : transportSystem
                ? <TransportSystemBody productKey={transportSystem.key} language={language} />
      : key === 'emotrace' ? <EmotraceBody /> : key === 'dc' ? <DcBody language={language} /> : <X12Body language={language} />}
    {isPilot && <RelatedProducts productId={productId as PilotProductId} language={language} />}
    <section className="product-contact" aria-labelledby="product-contact-title"><div className="shell-container">
      <div><h2 id="product-contact-title">{t('productPage.contactTitle', { product: title })}</h2><p>{t('productPage.contactDescription')}</p></div>
      <LocalizedLink className="button product-contact-link" to={`${getLocalizedPath('contacts', language)}?${query}`} state={{ productId, subject }}>{t('productPage.contactAction')}</LocalizedLink>
    </div></section>
  </div>
}
